import { Box, Center, Flex, Spinner, Text, useToast } from '@chakra-ui/react'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import ChatHeader from '../components/ChatHeader'
import { ChatInterface } from '../components/ChatInterface/ChatInterface'
import { SidebarToggleButton } from '../components/Sidebar'
import ChatSidebar from '../components/Sidebar/ChatSidebar'

import { useAgents } from '../hooks/agents/useAgents'

import { useChat } from '../hooks/chat/useChat'
import { useChatLogic } from '../hooks/chat/useChatLogic'
import { useLanguage } from '../hooks/utility/useLanguage'
import useMobile from '../hooks/utility/useMobile'
import { parseChatToMessageThread } from '../utils/threadUtil'

const ChatPage = () => {
  const navigate = useNavigate()
  const toast = useToast()

  const isMobile = useMobile()
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(true) // Default open for desktop

  const { agentId } = useParams()
  const { chatId: currentChatId } = useParams()
  const queryClient = useQueryClient()
  const { t } = useLanguage()

  // Use the chat details hook with the chat ID from URL
  const { chat: selectedChat, isLoading: isLoadingChatDetails } = useChat(currentChatId)

  // Callback for when a message is sent successfully to invalidate TanStack Query cache
  const handleMessageSentSuccessfully = useCallback(
    (messageId: string, currentChatId: string) => {
      queryClient.invalidateQueries({ queryKey: ['chat', currentChatId] })
    },
    [queryClient],
  )

  // Initialize the unified chat logic hook
  const {
    conversation,
    setConversation,
    clearConversation,
    clearConversationAndRegenerateChatId,
    isTyping,
    setIsTyping,
    isLoading: isStreaming,
    sendMessage,
    handleRetry,
    toggleResponseMinimized,
    chatContainerRef,
    isInputDisabled: isHookInputDisabled,
    hasDocumentsInConversation,
    currentPhase,
  } = useChatLogic({
    existingChatId: currentChatId,
    onMessageSentSuccessfully: handleMessageSentSuccessfully,
    isNewChatSession: false,
    agentId: agentId,
  })

  // Effect to update conversation when selectedChat changes
  useEffect(() => {
    if (selectedChat && selectedChat.messages && selectedChat.messages.length > 0) {
      const formattedThread = parseChatToMessageThread(selectedChat)
      setConversation(formattedThread)
    } else if (!currentChatId) {
      // Clear conversation when on homepage (no chatId)
      clearConversation()
    }
  }, [selectedChat, currentChatId, setConversation, clearConversation])

  // Get agents data
  const { agents } = useAgents()

  // Sync agent from chat data with context (important for page refresh and legacy routes)
  useEffect(() => {
    if (selectedChat && selectedChat.agentId && agents.length > 0) {
      const chatAgent = agents.find(agent => agent.id === selectedChat.agentId)

      if (!chatAgent) {
        // Chat belongs to an agent that no longer exists
        toast({
          title: t('errors.agentNotFound'),
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        })
        navigate('/', { replace: true })
        return
      }

      // If we have an agent in the URL, check if it matches the chat's agent
      if (agentId && agentId !== selectedChat.agentId) {
        // URL agent doesn't match chat's agent - show error and redirect
        toast({
          title: t('errors.conversationNotFound'),
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        })
        navigate(`/a/${agentId}`, { replace: true })
        return
      }

      // For legacy routes (/c/{chatId}), redirect to agent-specific route
      if (!agentId) {
        navigate(`/a/${selectedChat.agentId}/c/${currentChatId}`, { replace: true })
      }
    }
  }, [selectedChat, agents, agentId, currentChatId, navigate, toast, t])

  const handleSelectChat = (selectedChatId: string, chatAgentId?: string) => {
    if (selectedChatId !== currentChatId) {
      // Clear conversation when switching to a different chat
      clearConversation()

      if (chatAgentId) {
        // Navigate to agent-specific chat route
        navigate(`/a/${chatAgentId}/c/${selectedChatId}`)
      } else {
        // Fallback for legacy routes without agent
        navigate(`/c/${selectedChatId}`)
      }
    }
  }

  const handleSelectAgent = (selectedAgentId: string) => {
    if (selectedAgentId !== agentId) {
      clearConversationAndRegenerateChatId()

      navigate(`/a/${selectedAgentId}`)
    }
  }

  const handleNewChat = () => {
    clearConversationAndRegenerateChatId()

    // Navigate to agent-specific home (agentId is always available)
    navigate(`/a/${agentId}`)
  }

  const handleSidebarToggle = () => {
    setIsSidebarOpen(!isSidebarOpen)
  }

  // Overall input disabled state, considering initial chat details loading and hook's own disable state
  const isInputDisabled = isHookInputDisabled || isLoadingChatDetails

  return (
    <Flex width='100vw' height='100vh' flexDirection='row' position='relative'>
      {/* Sidebar - Always render, but it slides out when closed */}
      <ChatSidebar
        onNewChat={handleNewChat}
        isLoadingDetails={isLoadingChatDetails}
        selectedConversationId={currentChatId}
        onSelectConversation={handleSelectChat}
        onSelectAgent={handleSelectAgent}
        isSidebarOpenExternal={isSidebarOpen}
        onSidebarToggle={handleSidebarToggle}
        onClearConversation={clearConversation}
      />

      {/* Floating toggle button when sidebar is closed */}
      {!isSidebarOpen && (
        <SidebarToggleButton isOpen={isSidebarOpen} onToggle={handleSidebarToggle} isFloating={true} />
      )}

      {/* Main content area */}
      <Flex
        flex='1'
        h='100vh'
        flexDirection='column'
        minW='0'
        ml={isSidebarOpen && !isMobile ? '300px' : '0'}
        transition='margin-left 0.3s ease-in-out'
      >
        <ChatHeader />
        <Box flex='1' overflowY='hidden' minHeight='0'>
          {isLoadingChatDetails ? (
            <Center h='100%' flexDirection='column'>
              <Spinner thickness='4px' speed='0.65s' emptyColor='gray.200' color='primary' size='xl' mb={4} />
              <Box textAlign='center'>
                <Text fontSize='xl' fontWeight='medium' color='gray.600'>
                  {t('chat.loading')}
                </Text>
                {selectedChat?.title && (
                  <Text fontSize='md' color='gray.500' mt={2}>
                    {`"${selectedChat.title}"`}
                  </Text>
                )}
              </Box>
            </Center>
          ) : (
            <ChatInterface
              conversation={conversation}
              chatContainerRef={chatContainerRef}
              isInputDisabled={isInputDisabled}
              isTyping={isTyping}
              onSend={sendMessage}
              onRetry={handleRetry}
              onTypingChange={setIsTyping}
              hasDocumentsInConversation={hasDocumentsInConversation}
              onToggleResponseMinimized={toggleResponseMinimized}
              currentPhase={currentPhase}
              isStreaming={isStreaming}
            />
          )}
        </Box>
      </Flex>

      {/* Mobile overlay backdrop */}
      {isMobile && isSidebarOpen && (
        <Box
          position='fixed'
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg='blackAlpha.600'
          zIndex={999}
          onClick={handleSidebarToggle}
        />
      )}

    </Flex>
  )
}

export default ChatPage
